import { LIMITS, simplifyPath } from '../annotationGeometry';

/*
 * Pixel work for the annotation canvas: pure functions on typed arrays, so they
 * run the same in the browser and in Jest (which has no 2D canvas).
 *
 *   analyseImage   CIE Lab, blurred luminance, Sobel gradient (0..1), an edge
 *                  threshold at the image's own 85th percentile, local texture
 *   magicWand      flood fill by colour distance (ΔE), stopping at strong edges
 *                  and at a change of texture
 *   captureLoop    the Xonix rule: walls = the traced loop (+ existing paint of
 *                  the same part), then every region the new loop encloses is
 *                  filled; where the walls cut the image into pieces that each
 *                  reach its border, all but the largest are filled
 *   maskToShapes   outline tracing (Moore neighbourhood) and Douglas-Peucker,
 *                  loosened until the result fits the storage limits
 *
 * Everything is in analysis pixels; shapes leave here normalised to 0..1.
 */
export const ANALYSIS_MAX_SIDE = 1024;

export function analysisSize(width, height, maxSide = ANALYSIS_MAX_SIDE) {
  if (!width || !height) return { width: 1000, height: 750 };
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

const LINEAR = new Float32Array(256).map((_, i) => {
  const c = i / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
});
const labF = t => (t > 216 / 24389 ? Math.cbrt(t) : ((24389 / 27) * t + 16) / 116);

export function toLab(rgba, w, h) {
  const n = w * h;
  const L = new Float32Array(n);
  const A = new Float32Array(n);
  const B = new Float32Array(n);
  for (let i = 0; i < n; i += 1) {
    const r = LINEAR[rgba[i * 4]];
    const g = LINEAR[rgba[i * 4 + 1]];
    const b = LINEAR[rgba[i * 4 + 2]];
    const fx = labF((0.4124 * r + 0.3576 * g + 0.1805 * b) / 0.95047);
    const fy = labF(0.2126 * r + 0.7152 * g + 0.0722 * b);
    const fz = labF((0.0193 * r + 0.1192 * g + 0.9505 * b) / 1.08883);
    L[i] = 116 * fy - 16;
    A[i] = 500 * (fx - fy);
    B[i] = 200 * (fy - fz);
  }
  return { L, A, B };
}

export function gaussianBlur(src, w, h, sigma = 1.5) {
  const r = Math.max(1, Math.ceil(sigma * 3));
  const kernel = new Float32Array(2 * r + 1);
  let sum = 0;
  for (let i = -r; i <= r; i += 1) {
    kernel[i + r] = Math.exp(-(i * i) / (2 * sigma * sigma));
    sum += kernel[i + r];
  }
  for (let i = 0; i < kernel.length; i += 1) kernel[i] /= sum;
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y += 1) {
    const row = y * w;
    for (let x = 0; x < w; x += 1) {
      let acc = 0;
      for (let k = -r; k <= r; k += 1) acc += src[row + Math.min(w - 1, Math.max(0, x + k))] * kernel[k + r];
      tmp[row + x] = acc;
    }
  }
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      let acc = 0;
      for (let k = -r; k <= r; k += 1) acc += tmp[Math.min(h - 1, Math.max(0, y + k)) * w + x] * kernel[k + r];
      out[y * w + x] = acc;
    }
  }
  return out;
}

export function sobel(src, w, h) {
  const out = new Float32Array(w * h);
  let max = 0;
  const at = (x, y) => src[Math.min(h - 1, Math.max(0, y)) * w + Math.min(w - 1, Math.max(0, x))];
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const gx = at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1) - at(x - 1, y - 1) - 2 * at(x - 1, y) - at(x - 1, y + 1);
      const gy = at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1) - at(x - 1, y - 1) - 2 * at(x, y - 1) - at(x + 1, y - 1);
      const m = Math.hypot(gx, gy);
      out[y * w + x] = m;
      if (m > max) max = m;
    }
  }
  if (max > 0) for (let i = 0; i < out.length; i += 1) out[i] /= max;
  return out;
}

export function percentile(values, p) {
  const bins = new Uint32Array(1024);
  for (let i = 0; i < values.length; i += 1) bins[Math.min(1023, Math.max(0, Math.floor(values[i] * 1023)))] += 1;
  const target = p * values.length;
  let seen = 0;
  for (let b = 0; b < 1024; b += 1) {
    seen += bins[b];
    if (seen >= target) return b / 1023;
  }
  return 1;
}

export function localStd(src, w, h, radius = 2) {
  const W = w + 1;
  const sum = new Float64Array(W * (h + 1));
  const sq = new Float64Array(W * (h + 1));
  for (let y = 0; y < h; y += 1) {
    let rowSum = 0;
    let rowSq = 0;
    for (let x = 0; x < w; x += 1) {
      const v = src[y * w + x];
      rowSum += v;
      rowSq += v * v;
      sum[(y + 1) * W + x + 1] = sum[y * W + x + 1] + rowSum;
      sq[(y + 1) * W + x + 1] = sq[y * W + x + 1] + rowSq;
    }
  }
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y += 1) {
    const y0 = Math.max(0, y - radius);
    const y1 = Math.min(h, y + radius + 1);
    for (let x = 0; x < w; x += 1) {
      const x0 = Math.max(0, x - radius);
      const x1 = Math.min(w, x + radius + 1);
      const n = (x1 - x0) * (y1 - y0);
      const s = sum[y1 * W + x1] - sum[y0 * W + x1] - sum[y1 * W + x0] + sum[y0 * W + x0];
      const q = sq[y1 * W + x1] - sq[y0 * W + x1] - sq[y1 * W + x0] + sq[y0 * W + x0];
      out[y * w + x] = Math.sqrt(Math.max(0, q / n - (s / n) ** 2));
    }
  }
  return out;
}

export function analyseImage(rgba, w, h) {
  const lab = toLab(rgba, w, h);
  const grad = sobel(gaussianBlur(lab.L, w, h, 1.5), w, h);
  // A sharper gradient for the live wire: at sigma 1.5 the edge band is ~4 px wide
  // and the cheapest path cuts corners through it (measured: 86% of a rectangle's
  // corner-to-corner path on the edge, worst 4.5 px off); at 0.7 it is 100%, worst 1 px.
  const edge = sobel(gaussianBlur(lab.L, w, h, 0.7), w, h);
  return { width: w, height: h, lab, grad, edge, threshold: Math.max(0.08, percentile(grad, 0.85)), std: localStd(lab.L, w, h, 2) };
}

const clampInt = (v, lo, hi) => Math.min(hi, Math.max(lo, Math.floor(v)));

/** `allow` (optional, w x h): pixels where it is 0 are never filled nor crossed -- the
 *  Culture analysis page keeps a fill inside the dish (utils/masks/dishTools.js). */
export function magicWand(analysis, sx, sy, { tolerance = 12, textureTolerance = 10, edgeStop, allow = null } = {}) {
  const { width: w, height: h, lab, grad, std } = analysis;
  const stop = edgeStop ?? Math.max(analysis.threshold * 1.6, 0.3);
  const seed = clampInt(sy, 0, h - 1) * w + clampInt(sx, 0, w - 1);
  if (allow && !allow[seed]) return new Uint8Array(w * h);
  const L0 = lab.L[seed];
  const A0 = lab.A[seed];
  const B0 = lab.B[seed];
  const S0 = std[seed];
  const tol2 = tolerance * tolerance;
  // Colour very close to the seed is always the same region: the blurred edge
  // band and the texture window straddle a boundary by a few pixels, and
  // without this they would shave a ring off every region's rim.
  const sure2 = tol2 / 4;
  const mask = new Uint8Array(w * h);
  const queue = new Int32Array(w * h);
  let head = 0;
  let tail = 0;
  mask[seed] = 1;
  queue[tail++] = seed;
  const consider = (n) => {
    if (mask[n] || (allow && !allow[n])) return;
    const dL = lab.L[n] - L0;
    const dA = lab.A[n] - A0;
    const dB = lab.B[n] - B0;
    const d2 = dL * dL + dA * dA + dB * dB;
    if (d2 <= sure2 || (d2 <= tol2 && grad[n] <= stop && Math.abs(std[n] - S0) <= textureTolerance)) {
      mask[n] = 1;
      queue[tail++] = n;
    }
  };
  while (head < tail) {
    const i = queue[head++];
    const x = i % w;
    if (x > 0) consider(i - 1);
    if (x < w - 1) consider(i + 1);
    if (i >= w) consider(i - w);
    if (i < w * (h - 1)) consider(i + w);
  }
  return mask;
}

export function countMask(mask) {
  let n = 0;
  for (let i = 0; i < mask.length; i += 1) n += mask[i] ? 1 : 0;
  return n;
}

export function labelComponents(mask, w, h, connectivity = 4) {
  const labels = new Int32Array(w * h);
  const sizes = [0];
  const touchesBorder = [false];
  const queue = new Int32Array(w * h);
  let next = 1;
  for (let i = 0; i < w * h; i += 1) {
    if (!mask[i] || labels[i]) continue;
    let head = 0;
    let tail = 0;
    let size = 0;
    let border = false;
    labels[i] = next;
    queue[tail++] = i;
    while (head < tail) {
      const p = queue[head++];
      size += 1;
      const x = p % w;
      const y = (p - x) / w;
      if (x === 0 || y === 0 || x === w - 1 || y === h - 1) border = true;
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          if ((!dx && !dy) || (connectivity === 4 && dx && dy)) continue;
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          const n = ny * w + nx;
          if (mask[n] && !labels[n]) {
            labels[n] = next;
            queue[tail++] = n;
          }
        }
      }
    }
    sizes.push(size);
    touchesBorder.push(border);
    next += 1;
  }
  return { labels, sizes, touchesBorder, count: next - 1 };
}

/**
 * A 3 x 3 dilation (grow) or erosion, as two passes -- along the rows, then
 * down the columns -- which is the same as looking at all nine neighbours
 * and about ten times faster on a large picture. Outside the picture counts
 * as empty, so an erosion clears the border.
 */
function morph(mask, w, h, grow) {
  const rows = new Uint8Array(w * h);
  for (let y = 0; y < h; y += 1) {
    const o = y * w;
    for (let x = 0; x < w; x += 1) {
      const c = mask[o + x] ? 1 : 0;
      const l = x > 0 && mask[o + x - 1] ? 1 : 0;
      const r = x < w - 1 && mask[o + x + 1] ? 1 : 0;
      rows[o + x] = grow ? (l | c | r) : (l & c & r);
    }
  }
  const out = new Uint8Array(w * h);
  for (let y = 0; y < h; y += 1) {
    const o = y * w;
    for (let x = 0; x < w; x += 1) {
      const c = rows[o + x];
      const u = y > 0 ? rows[o - w + x] : 0;
      const d = y < h - 1 ? rows[o + w + x] : 0;
      out[o + x] = grow ? (u | c | d) : (u & c & d);
    }
  }
  return out;
}

export const dilate = (mask, w, h) => morph(mask, w, h, true);
export const erode = (mask, w, h) => morph(mask, w, h, false);

export function fillHoles(mask, w, h, maxHoleFraction = 0.005) {
  const background = new Uint8Array(w * h);
  let foreground = 0;
  for (let i = 0; i < mask.length; i += 1) {
    background[i] = mask[i] ? 0 : 1;
    foreground += mask[i] ? 1 : 0;
  }
  const { labels, sizes, touchesBorder } = labelComponents(background, w, h, 4);
  const limit = Math.max(1, foreground * maxHoleFraction);
  const out = mask.slice();
  for (let i = 0; i < labels.length; i += 1) {
    const l = labels[i];
    if (l && !touchesBorder[l] && sizes[l] <= limit) out[i] = 1;
  }
  return out;
}

export function removeSpecks(mask, w, h, minFraction = 0.02) {
  const { labels, sizes, count } = labelComponents(mask, w, h, 8);
  if (!count) return mask.slice();
  let largest = 0;
  for (let l = 1; l < sizes.length; l += 1) largest = Math.max(largest, sizes[l]);
  const min = Math.max(9, largest * minFraction);
  const out = new Uint8Array(w * h);
  for (let i = 0; i < labels.length; i += 1) out[i] = labels[i] && sizes[labels[i]] >= min ? 1 : 0;
  return out;
}

/** Close 1-px gaps, fill small holes, drop stray specks. */
export function cleanMask(mask, w, h) {
  return removeSpecks(fillHoles(erode(dilate(mask, w, h), w, h), w, h), w, h);
}

export function stampDisc(mask, w, h, cx, cy, radius, value = 1) {
  const r = Math.max(0.75, radius);
  const r2 = r * r;
  for (let y = Math.max(0, Math.floor(cy - r)); y <= Math.min(h - 1, Math.ceil(cy + r)); y += 1) {
    for (let x = Math.max(0, Math.floor(cx - r)); x <= Math.min(w - 1, Math.ceil(cx + r)); x += 1) {
      const dx = x + 0.5 - cx;
      const dy = y + 0.5 - cy;
      if (dx * dx + dy * dy <= r2) mask[y * w + x] = value;
    }
  }
  const px = Math.floor(cx);
  const py = Math.floor(cy);
  if (px >= 0 && py >= 0 && px < w && py < h) mask[py * w + px] = value;
}

export function rasterizePolyline(mask, w, h, points, thickness = 2, closed = false, value = 1) {
  if (!points.length) return mask;
  const radius = Math.max(0.75, thickness / 2);
  const pts = closed && points.length > 2 ? [...points, points[0]] : points;
  if (pts.length === 1) stampDisc(mask, w, h, pts[0][0], pts[0][1], radius, value);
  for (let i = 0; i + 1 < pts.length; i += 1) {
    const [ax, ay] = pts[i];
    const [bx, by] = pts[i + 1];
    const steps = Math.max(1, Math.ceil(Math.hypot(bx - ax, by - ay) / 0.5));
    for (let s = 0; s <= steps; s += 1) {
      const t = s / steps;
      stampDisc(mask, w, h, ax + (bx - ax) * t, ay + (by - ay) * t, radius, value);
    }
  }
  return mask;
}

export function rasterizePolygon(mask, w, h, points, value = 1) {
  const n = points.length;
  if (n < 3) return mask;
  let minY = Infinity;
  let maxY = -Infinity;
  points.forEach(([, y]) => { minY = Math.min(minY, y); maxY = Math.max(maxY, y); });
  for (let y = Math.max(0, Math.floor(minY)); y <= Math.min(h - 1, Math.ceil(maxY)); y += 1) {
    const sy = y + 0.5;
    const xs = [];
    for (let i = 0, j = n - 1; i < n; j = i, i += 1) {
      const [xi, yi] = points[i];
      const [xj, yj] = points[j];
      if ((yi > sy) !== (yj > sy)) xs.push(xi + ((sy - yi) * (xj - xi)) / (yj - yi));
    }
    xs.sort((a, b) => a - b);
    for (let k = 0; k + 1 < xs.length; k += 2) {
      const x0 = Math.max(0, Math.ceil(xs[k] - 0.5));
      const x1 = Math.min(w - 1, Math.floor(xs[k + 1] - 0.5));
      for (let x = x0; x <= x1; x += 1) mask[y * w + x] = value;
    }
  }
  return mask;
}

/** The painted area of stored shapes (normalised coordinates), erasers included. */
export function rasterizeShapes(shapes, w, h) {
  const mask = new Uint8Array(w * h);
  (shapes || []).forEach(shape => {
    const value = shape.erase ? 0 : 1;
    const pts = shape.points.map(([x, y]) => [x * w, y * h]);
    if (shape.kind === 'polygon') rasterizePolygon(mask, w, h, pts, value);
    else rasterizePolyline(mask, w, h, pts, (shape.width || 0.01) * w, false, value);
  });
  return mask;
}

/** Free regions that the new path helps enclose (see the header for the rule). */
export function enclosedFill(walls, path, w, h) {
  const free = new Uint8Array(w * h);
  for (let i = 0; i < free.length; i += 1) free[i] = walls[i] ? 0 : 1;
  const { labels, sizes, touchesBorder } = labelComponents(free, w, h, 4);
  const near = dilate(dilate(path, w, h), w, h);
  const adjacent = new Set();
  for (let i = 0; i < near.length; i += 1) if (near[i] && labels[i]) adjacent.add(labels[i]);
  const interior = [...adjacent].filter(l => !touchesBorder[l]);
  const edge = [...adjacent].filter(l => touchesBorder[l]).sort((a, b) => sizes[a] - sizes[b]);
  const chosen = new Set([...interior, ...(edge.length > 1 ? edge.slice(0, -1) : [])]);
  const fill = new Uint8Array(w * h);
  if (!chosen.size) return fill;
  for (let i = 0; i < labels.length; i += 1) if (chosen.has(labels[i])) fill[i] = 1;
  return fill;
}

/**
 * The area a traced path claims, or null when it encloses nothing.
 * `existing` are the current shapes of the same part and specimen; with `xonix`
 * they act as walls, so a path that leaves and returns to that paint fills the gap.
 */
export function captureLoop({ points, w, h, existing = [], xonix = true, close = true, minArea }) {
  if (!points || points.length < (close ? 3 : 2)) return null;
  const path = rasterizePolyline(new Uint8Array(w * h), w, h, points, 2, close);
  const existingMask = xonix && existing.length ? rasterizeShapes(existing, w, h) : new Uint8Array(w * h);
  const walls = new Uint8Array(w * h);
  for (let i = 0; i < walls.length; i += 1) walls[i] = path[i] || existingMask[i] ? 1 : 0;
  const enclosed = enclosedFill(walls, path, w, h);
  if (!countMask(enclosed)) return null;
  const claimed = new Uint8Array(w * h);
  for (let i = 0; i < claimed.length; i += 1) claimed[i] = (enclosed[i] || path[i]) && !existingMask[i] ? 1 : 0;
  const mask = cleanMask(claimed, w, h);
  const area = countMask(mask);
  if (area < (minArea ?? Math.max(12, 0.0005 * w * h))) return null;
  return { mask, area };
}

const DIRECTIONS = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];
const directionOf = (dx, dy) => DIRECTIONS.findIndex(([a, b]) => a === dx && b === dy);

/** The outer boundary of every 8-connected component, in pixel-centre coordinates. */
export function traceContours(mask, w, h, minPixels = 4) {
  const { labels, sizes } = labelComponents(mask, w, h, 8);
  const done = new Uint8Array(sizes.length);
  const contours = [];
  for (let i = 0; i < labels.length; i += 1) {
    const label = labels[i];
    if (!label || done[label]) continue;
    done[label] = 1;
    if (sizes[label] < minPixels) continue;
    const sx = i % w;
    const sy = (i - sx) / w;
    const inside = (x, y) => x >= 0 && y >= 0 && x < w && y < h && labels[y * w + x] === label;
    const points = [[sx + 0.5, sy + 0.5]];
    let c = [sx, sy];
    let b = [sx - 1, sy];
    const limit = 4 * sizes[label] + 16;
    for (let step = 0; step < limit; step += 1) {
      const from = directionOf(b[0] - c[0], b[1] - c[1]);
      let moved = false;
      for (let k = 1; k <= 8; k += 1) {
        const d = (from + k) % 8;
        const nx = c[0] + DIRECTIONS[d][0];
        const ny = c[1] + DIRECTIONS[d][1];
        if (inside(nx, ny)) {
          const pd = (from + k + 7) % 8;
          b = [c[0] + DIRECTIONS[pd][0], c[1] + DIRECTIONS[pd][1]];
          c = [nx, ny];
          moved = true;
          break;
        }
      }
      if (!moved) break;
      if (c[0] === sx && c[1] === sy && b[0] === sx - 1 && b[1] === sy) break;
      points.push([c[0] + 0.5, c[1] + 0.5]);
    }
    contours.push(points);
  }
  return contours;
}

/** Polygons (normalised) for a mask, simplified until they fit the storage limits. */
export function maskToShapes(mask, w, h, { erase = false, maxShapes = 50 } = {}) {
  const contours = traceContours(mask, w, h);
  let shapes = [];
  for (let attempt = 0; attempt < 10; attempt += 1) {
    const tolerance = 0.0008 * 2 ** attempt;
    shapes = contours
      .map(points => ({ kind: 'polygon', points: simplifyPath(points.map(([x, y]) => [x / w, y / h]), tolerance),
                        ...(erase ? { erase: true } : {}) }))
      .filter(shape => shape.points.length >= 3);
    const total = shapes.reduce((n, s) => n + s.points.length, 0);
    if (shapes.every(s => s.points.length <= LIMITS.maxPoints) && total <= LIMITS.maxTotalPoints / 4) break;
  }
  return shapes.slice(0, maxShapes);
}

/**
 * The photograph's bytes as an image the canvas may read back from, when a
 * plain cross-origin <img> would taint it: fetched with CORS and no cache
 * (a cached copy fetched earlier without CORS headers is what usually fails),
 * decoded from a blob -- which is same-origin, so drawing it never taints a
 * canvas. Resolves to an ImageBitmap where the browser has one, else an
 * HTMLImageElement on an object URL. Rejects when the origin refuses CORS.
 */
export function fetchPixelImage(src, { fetchFn = globalThis.fetch, bitmap = globalThis.createImageBitmap } = {}) {
  if (typeof fetchFn !== 'function') return Promise.reject(new Error('no fetch'));
  return fetchFn(src, { mode: 'cors', credentials: 'omit', cache: 'reload' })
    .then((res) => {
      if (!res || !res.ok) throw new Error(`photo ${res ? res.status : 'unreachable'}`);
      return res.blob();
    })
    .then((blob) => {
      if (typeof bitmap === 'function') return bitmap(blob);
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('undecodable photo')); };
        img.src = url;
      });
    });
}
